import { ButtonCategory } from "@components/atoms/Button/types";
import { useCallback, useMemo, useState } from "react";
import { Diamond } from "@src/types/DiamondDetail";
import { EXPORTED_TYPES } from "../types";
import {
  EventName,
  EventType,
  getAnalyticsProductData,
} from "@utils/analytics";
import { useUserAnalytics } from "../../../../hooks/useAnalytics";
import { InteractionManager, Linking, Platform } from "react-native";
import { useItemMedia } from "@apis/share";
import apiClient from "@apis/index";
import Routes from "@apis/routes";

// Lazy getter for FileSystem - only loads when actually needed on native
// Using the new File/Directory/Paths API (SDK 54+)
let _fileSystemModule: {
  File: any;
  Directory: any;
  Paths: any;
} | null = null;

const getFileSystemModule = (): typeof _fileSystemModule => {
  if (Platform.OS === "web") return null;
  if (!_fileSystemModule) {
    try {
      const fs = require("expo-file-system");
      _fileSystemModule = {
        File: fs.File,
        Directory: fs.Directory,
        Paths: fs.Paths,
      };
    } catch (e) {
      console.warn("expo-file-system not available");
      _fileSystemModule = null;
    }
  }
  return _fileSystemModule;
};

export type BulkShareState = {
  bulkImageShare: boolean;
  bulkReportsShare: boolean;
};

export type ShareExcelModalProps = {
  isOpen: boolean;
  enableToastMessage: boolean;
  onClose: () => void;
  fromFavoriteScreen: boolean;
};
export const getButtonCategory = (
  productType: string | undefined
): ButtonCategory => {
  switch (productType) {
    case "diamond":
      return ButtonCategory.diam;
    case "gemstone":
      return ButtonCategory.gem;
    case "jewelry":
      return ButtonCategory.jewelry;
    case "lab_grown_diamond":
      return ButtonCategory.lab;
    default:
      return ButtonCategory.default;
  }
};

export const useCheckboxState = (initialState: any) => {
  const [checkedState, setCheckedState] = useState(initialState);
  const [userHasUnchecked, setUserHasUnchecked] = useState(false);

  const handleCheckboxChange = useCallback(() => {
    setCheckedState((prevState: any) => !prevState);
    setUserHasUnchecked(!userHasUnchecked);
  }, [userHasUnchecked]);

  return [checkedState, handleCheckboxChange];
};

export const useItemInfo = (items: Diamond[] | undefined) => {
  const itemCount = items?.length;
  const bulkItemIds = items?.map((item) => item.id);

  const getTotalSalesPrice = () => {
    return items?.reduce((totalSalesPrice, item) => {
      return totalSalesPrice + Number(item.total_sales_price);
    }, 0);
  };

  const totalSalesPrice = getTotalSalesPrice();

  return { itemCount, bulkItemIds, totalSalesPrice };
};

export enum ShareVia {
  EMAIL = "email",
  NATIVE = "native",
  MEDIA = "media",
  LINK = "link",
}

const waitForUiSettle = () =>
  new Promise<void>((resolve) => {
    InteractionManager.runAfterInteractions(() => {
      setTimeout(resolve, 500);
    });
  });

export const useShareContent = (onClose?: () => void) => {
  const shareContent = async (
    responseData: {
      data?: { title: string; link: string };
      link?: string;
      link_title?: string;
      title?: string;
      deep_link?: string;
      stock_item_request_deep_link?: string;
      shareable_link?: { title: string; link: string };
      additional_attributes?: {
        link?: string;
        link_title?: string;
        title?: string;
      };
    },
    isBulkShare = false,
    isSingleShare = false,
    fromBulkList = false,
    Share: any
  ) => {
    let rawTitle: string | undefined;
    let rawLink: string | undefined;

    if (isBulkShare || isSingleShare) {
      rawTitle = responseData.data?.title;
      rawLink = responseData.data?.link;
      if (isSingleShare) {
        [rawTitle, rawLink] = [rawLink, rawTitle];
      }
    } else if (fromBulkList) {
      const source =
        responseData?.shareable_link ??
        responseData?.additional_attributes ??
        responseData;
      rawTitle =
        ("link_title" in source ? source.link_title : undefined) ??
        source?.title;
      rawLink =
        source?.link ??
        responseData?.stock_item_request_deep_link ??
        responseData?.deep_link;
    }

    if (!rawTitle && !rawLink) {
      console.error("Error sharing: missing title and link", responseData);
      return;
    }

    const actualTitle = rawTitle?.includes("https") ? rawLink : rawTitle;
    const actualLink = rawTitle?.includes("https") ? rawTitle : rawLink;

    const shareText = `Title - ${actualTitle}\nLink: ${actualLink}`;
    const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
      `${actualLink} ${actualTitle}`
    )}`;

    try {
      await waitForUiSettle();

      const shareOptions = { message: shareText, failOnCancel: false };

      if (Share) {
        await Share.open(shareOptions);
      } else {
        await Linking.openURL(whatsappUrl);
        onClose?.();
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  return { shareContent };
};

const whatsappItemShare = (mediaItem: any) => {
  const link = encodeURIComponent(mediaItem);
  const whatsappUrl = `https://api.whatsapp.com/send?text=${link}`;
  const whatsappFallbackUrl = `https://wa.me/?text=${link}`;
  try {
    window.open(whatsappUrl, "_blank");
  } catch (e) {
    window.open(whatsappFallbackUrl, "_blank");
  }
};

export const useNativeShare = (mItem?: any) => {
  let Share: any =
    Platform.OS !== "web" ? require("react-native-share").default : undefined;
  const [nativeShareLoading, setNativeShareLoading] = useState(false);
  const { trackButtonEvent } = useUserAnalytics();
  const handleShareViaNative = async (mediaItem: string) => {
    if (!mediaItem) return;

    setNativeShareLoading(true);
    let localUri: string | null = null;
    try {
      trackButtonEvent(
        EventName.BUTTON_CLICK,
        EventType.SHARE_VIA_APP,
        getAnalyticsProductData(mItem)
      );

      const url = mediaItem;
      const fileExtension = url.split(".").pop()?.toLowerCase() ?? "";
      const isExportedType = fileExtension
        ? Object.keys(EXPORTED_TYPES).includes(fileExtension)
        : false;

      if (isExportedType) {
        const fileName = url.split("/").pop() || `shared.${fileExtension}`;
        if (Platform.OS === "web") {
          try {
            const response = await apiClient.get(Routes.DOWNLOAD_FILE, {
              params: { url },
              responseType: "blob",
            });
            const contentType =
              response.headers["content-type"] || "application/octet-stream";

            if (contentType.includes("application/json")) {
              const errorData = JSON.parse(await response.data.text());
              if (errorData?.status && errorData.status !== 200) {
                whatsappItemShare(mediaItem);
                return;
              }
            }

            const blob = new Blob([response.data], { type: contentType });
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          } catch (e) {
            console.error(`Download failed for ${url}:`, e);
            whatsappItemShare(mediaItem);
          }
          return;
        }
        const fsModule = getFileSystemModule();
        if (fsModule) {
          const { File, Directory, Paths } = fsModule;
          const cacheDir = new Directory(Paths.cache);

          try {
            const downloadedFile = await File.downloadFileAsync(url, cacheDir, {
              idempotent: true,
            });
            localUri = downloadedFile.uri;
          } catch (error) {
            console.error(`Error downloading ${url}: ${error}`);
          }
        }
      } else if (Platform.OS === "web") {
        whatsappItemShare(mediaItem);
      }

      const shareOptions = {
        title: mItem?.short_title,
        url: localUri ?? url,
        ...(Platform.OS === "android" && {
          type: fileExtension ? `image/${fileExtension}` : undefined,
        }),
        failOnCancel: false,
      };

      await Share.open(shareOptions);
    } catch (error) {
      console.error("Error sharing:", error);
    } finally {
      setNativeShareLoading(false);
    }
  };

  return {
    nativeShareLoading,
    handleShareViaNative,
  };
};

export const useCheckboxHandlers = (
  setCheckedItems: React.Dispatch<React.SetStateAction<any>>,
  checkedItems: any
) => {
  const handlers = useMemo(() => {
    const createHandler = (type: any) => {
      const handleCheckboxChange = (order?: string | number) => {
        if (type === "videos") {
          setCheckedItems((prevState) => ({
            ...prevState,
            videos: !prevState.videos,
          }));
        } else {
          setCheckedItems((prevState) => ({
            ...prevState,
            [type]: {
              ...prevState[type],
              [order!]: !prevState[type][order!],
            },
          }));
        }
      };

      const handleAllChecked = () => {
        if (type === "videos") {
          setCheckedItems((prevState) => ({
            ...prevState,
            videos: !prevState.videos,
          }));
        } else {
          setCheckedItems((prevState) => {
            const newCheckedItems = { ...prevState[type] };
            const currentValue = Object.values(newCheckedItems).some(
              (val) => val
            );
            Object.keys(newCheckedItems).forEach((key) => {
              newCheckedItems[key] = !currentValue;
            });
            return {
              ...prevState,
              [type]: newCheckedItems,
            };
          });
        }
      };

      const isAllChecked =
        type === "videos"
          ? checkedItems.videos
          : Object.values(checkedItems[type]).some((val) => val);

      return {
        handleCheckboxChange,
        handleAllChecked,
        isAllChecked,
      };
    };
    return {
      images: createHandler("images"),
      reports: createHandler("reports"),
      videos: createHandler("videos"),
    };
  }, [setCheckedItems, checkedItems]);

  return handlers;
};

export const useProcessItemMedia = (
  itemIds: [string | undefined, string | undefined] | [string | undefined]
) => {
  const { data: itemMediaResponse } = useItemMedia(itemIds);
  const itemMediaData = itemMediaResponse?.data?.data;

  return useMemo(() => {
    const itemsData =
      itemMediaData?.filter((item) => itemIds.includes(item.itemId)) || [];

    let globalOrder = 1;
    const allMedia: any[] = [];

    for (const item of itemsData) {
      for (const mediaItem of item.mediaList || []) {
        allMedia.push({
          ...mediaItem,
          order: globalOrder++,
        });
      }
    }

    const processedResult = {
      mediaList: allMedia,
      sortedImageItems: allMedia
        .filter((mediaItem) => mediaItem.type === "image")
        .sort((a, b) => a.globalOrder - b.globalOrder),
      sortedVideoItems: allMedia
        .filter((mediaItem) => mediaItem.type === "video")
        .sort((a, b) => a.globalOrder - b.globalOrder),
      sortedReports: allMedia
        .filter((mediaItem) => mediaItem.type === "pdf")
        .sort((a, b) => a.globalOrder - b.globalOrder),
      itemMediaData,
    };
    return processedResult;
  }, [itemMediaData, itemIds]);
};

export const mapOptionKeysToNames = (
  optionsArray,
  inputObj,
  visibleOptions,
  fromReviewPage
) => {
  if (fromReviewPage) {
    if (optionsArray) {
      const visibleFromRef =
        Array.isArray(visibleOptions) && visibleOptions.length > 0
          ? new Set(visibleOptions)
          : null;

      const typeNameMap = optionsArray.reduce((acc, option) => {
        acc[option.option_type] = option.option_name;
        return acc;
      }, {});

      const configOptionTypes = new Set(optionsArray.map((o) => o.option_type));

      const result = {};
      Object.entries(inputObj).forEach(([key, value]) => {
        if (
          visibleFromRef &&
          configOptionTypes.has(key) &&
          !visibleFromRef.has(key)
        ) {
          return;
        }
        const newKey = typeNameMap[key] || key;
        result[newKey] = value;
      });
      return result;
    } else return inputObj;
  }
};

export const mapKeysToLabels = (personalizationObj: any, schemaObj: any) => {
  if (!personalizationObj || !schemaObj) return {};
  const result = {};

  Object.values(schemaObj).forEach((field) => {
    const key = field.name;
    const label = field.label;

    if (personalizationObj.hasOwnProperty(key)) {
      result[label] = personalizationObj[key];
    }
  });

  return result;
};
